/**
 * @file Effets visuels pilotes par les evenements du core : muzzle flash, impacts, explosions, chute d'arbre (feuilles), confettis, billets aspires,
 *       traines de munitions. Les skins de munitions (AmmoStyle) choisissent couleurs, traine et jeu de particules d'impact.
 */
import {
  Color,
  DynamicDrawUsage,
  Euler,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Quaternion,
  TorusGeometry,
  Vector3,
} from 'three';
import type { BufferGeometry, Scene } from 'three';
import { CONFIG, RENDER } from '@config/index';
import type { AmmoStyle, FireworkStyle } from '@config/types';
import { TARGET_BY_ID } from '@config/targets';
import type { EventBus, GameEvents } from '@core/index';
import type { WeaponBehavior } from '@shared/enums';
import { buildSprite, SPRITE_SHAPES } from '../ammo/AmmoShapes';
import { colorOf, rainbowInto } from '../ammo/colors';
import type { CameraRig } from '../CameraRig';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import type { ToonKit } from '../toon/toon';

/** Familles de particules (forme, trajectoire, courbe de taille). */
enum Kind {
  Spark,
  Flash,
  Leaf,
  Bill,
  Confetti,
  Smoke,
  Glitter,
  Chip,
  Ember,
  Dot,
  Heart,
  Star,
  Sparkle,
  Petal,
  Bubble,
  Flake,
  Flame,
}

/** Couche (InstancedMesh) de chaque famille : 0 = icosaedres, 1.. = sprites face camera (SPRITE_SHAPES). */
const LAYER_OF: readonly number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 2, 3, 4, 5, 6, 7];
const LAYERS = 1 + SPRITE_SHAPES.length;
const FIRST_SPRITE = Kind.Dot + 1;

type MuzzleLookup = (catId: number) => { x: number; y: number; z: number } | null;

const RING_POOL = 6;
const RING_SECONDS = 0.42;
const BILL_HOMING_START = 0.32;
const GROUND_Y = 0.05;
const CASING_COLOR = '#e8c36a';
const BILL_COLOR = '#8ef060';
const CONFETTI_COLORS = ['#ff8fb8', '#ffd45e', '#7fe9ff', '#a5e58d', '#b79bff', '#ffffff'];
const EXPLOSION_COLORS = ['#ff9a3c', '#ffd24a', '#ff6a3c', '#fff1a8'];
const AXIS_Z = new Vector3(0, 0, 1);

// ---- feu d'artifice du hub
const MAX_ROCKETS = 8;
const MAX_TRAILERS = 48;
/** Altitude de depart de la fusee et sommet (au-dessus du sol, unites monde), duree de la montee. */
const ROCKET_START_Y = 1.5;
const ROCKET_PEAK_MIN = 6.4;
const ROCKET_PEAK_VAR = 1.6;
const ROCKET_SECONDS = 0.66;
const ROCKET_TRAIL_EVERY = 0.028;
/** Decalage entre deux fusees d'une salve (s). */
const SALVO_DELAY = 0.25;
/** Echelle de l'eclat selon `show.size` 1..3. */
const SHOW_SIZE_K = [1, 1.25, 1.6] as const;
/** Au-dela de ce nombre de particules vivantes, les traines secondaires (willow, palm...) sont suspendues : le feu reste sous ~150 particules. */
const FIREWORK_PARTICLE_BUDGET = 150;

/** Une fusee en vol (pool fixe, aucune allocation par frame). `t` < 0 : en attente (salve decalee). */
interface Rocket {
  active: boolean;
  t: number;
  dur: number;
  x: number;
  z: number;
  peak: number;
  trailAcc: number;
  style: AmmoStyle | null;
  /** Spectacle (null = feu classique du skin de munition). */
  show: FireworkStyle | null;
}

/**
 * Emetteur a trajectoire balistique (branches de saule / palmier / fontaines / crossettes) : integre sa position et seme des particules
 * derriere lui ; `split` : se divise en croix a la fin ; `crackle` : immobile, seme des etincelles scintillantes (crepitements).
 */
interface Trailer {
  active: boolean;
  t: number;
  dur: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  gravity: number;
  drag: number;
  acc: number;
  every: number;
  size: number;
  hue: number;
  color: string;
  rainbow: boolean;
  split: boolean;
  crackle: boolean;
  /** Rayon des crepitements ; type de particule semee (Sparkle pour les fontaines, Ember sinon). */
  radius: number;
  sparkle: boolean;
}

const TAU = Math.PI * 2;

const rnd = (a: number, b: number): number => a + Math.random() * (b - a);

/**
 * Systeme de particules : InstancedMesh d'icosaedres + un InstancedMesh par sprite (coeur, etoile, scintillement, petale, bulle, flocon ;
 * capacite RENDER.fx.maxParticles, x0.4 en mode Eco, un emplacement = une particule quel que soit son maillage). Chaque particule
 * = position, vitesse, vie, taille, couleur (instanceColor ecrite au spawn), gravite, trainee, rotation. Tout est alloue a la
 * construction (aucune allocation par frame). Anneaux d'explosion : petit pool de tores plats.
 * Abonnements (subscribe) :
 *  - ProjectileFired / BeamFired : flash de bouche (couleur du skin de la categorie) + douille ; faisceau : particules de traine le long.
 *  - EntityHit : impact (jeu de particules du skin : etincelles, flammes, confettis, coeurs, etoiles, bulles, petales, flocons).
 *  - Explosion : anneau (couleur du skin) + particules + fumee.
 *  - TargetKilled : arbre -> feuilles ; monstre -> "pouf" ; roche/minerai -> eclats ; or/rose -> paillettes ; argent -> billets vers le chef.
 *  - RewardShown (avec position) : billets.   LevelCleared : confettis.
 *  - Firework (hub) : salve de fusees (1-3) qui montent au-dessus du chef puis eclatent selon `show.pattern` (peony, ring, willow, heart, star,
 *    spiral, double, saturn, crossette, palm, cascade) ou, si `show.useAmmo`, en eclat classique aux couleurs / particules du skin de munition.
 *  - CatnipCollected : gerbe de particules vertes / violettes.
 *  - trail() : traine d'un projectile (appelee par ProjectileRenderer, echantillonnee).
 */
export class FxManager {
  private unsubs: Array<() => void> = [];
  private readonly layers: InstancedMesh[] = [];
  private readonly layerAlive = new Int32Array(LAYERS);
  private readonly layerDirty = new Uint8Array(LAYERS);
  private readonly capacity: number;
  private limit: number;
  private eco = false;
  /** Part des particules autorisee par le reglage Qualite (1 = toutes). */
  private detail = 1;
  // etat des particules (tableaux a taille fixe)
  private readonly px: Float32Array;
  private readonly py: Float32Array;
  private readonly pz: Float32Array;
  private readonly vx: Float32Array;
  private readonly vy: Float32Array;
  private readonly vz: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly size: Float32Array;
  private readonly gravity: Float32Array;
  private readonly drag: Float32Array;
  private readonly rot: Float32Array;
  private readonly spin: Float32Array;
  private readonly kind: Uint8Array;
  private readonly wasAlive: Uint8Array;
  private highWater = 0;
  private cursor = 0;
  private aliveCount = 0;
  private clock = 0;
  private billsAlive = 0;
  // fusees du feu d'artifice
  private readonly rockets: Rocket[] = Array.from({ length: MAX_ROCKETS }, () => ({ active: false, t: 0, dur: ROCKET_SECONDS, x: 0, z: 0, peak: ROCKET_PEAK_MIN, trailAcc: 0, style: null, show: null }));
  private readonly trailers: Trailer[] = Array.from({ length: MAX_TRAILERS }, () => ({
    active: false,
    t: 0,
    dur: 1,
    x: 0,
    y: 0,
    z: 0,
    vx: 0,
    vy: 0,
    vz: 0,
    gravity: 0,
    drag: 0,
    acc: 0,
    every: 0.05,
    size: 0.1,
    hue: 0,
    color: '#ffffff',
    rainbow: false,
    split: false,
    crackle: false,
    radius: 0,
    sparkle: false,
  }));
  private readonly camView = new Vector3();
  private readonly fwV = new Vector3();
  private readonly camRight = new Vector3();
  private readonly camUp = new Vector3();
  // anneaux
  private readonly rings: Array<{ mesh: Mesh; material: MeshBasicMaterial; t: number; radius: number }> = [];
  // helpers
  private leaderId: number | null = null;
  private muzzleOf: MuzzleLookup = () => null;
  private readonly color = new Color();
  private readonly m = new Matrix4();
  private readonly q = new Quaternion();
  private readonly qRoll = new Quaternion();
  private readonly e = new Euler();
  private readonly p = new Vector3();
  private readonly s = new Vector3();
  private readonly zero = new Matrix4().makeScale(0, 0, 0);
  private readonly leaderPos = new Vector3();

  constructor(
    private readonly scene: Scene,
    private readonly kit: ToonKit,
    private readonly geo: GeometryLibrary,
    private readonly camera: CameraRig,
    private readonly ammoStyleOf: (behavior: WeaponBehavior) => AmmoStyle,
  ) {
    const cap = RENDER.fx.maxParticles;
    this.capacity = cap;
    this.limit = cap;
    this.px = new Float32Array(cap);
    this.py = new Float32Array(cap);
    this.pz = new Float32Array(cap);
    this.vx = new Float32Array(cap);
    this.vy = new Float32Array(cap);
    this.vz = new Float32Array(cap);
    this.life = new Float32Array(cap);
    this.maxLife = new Float32Array(cap).fill(1);
    this.size = new Float32Array(cap);
    this.gravity = new Float32Array(cap);
    this.drag = new Float32Array(cap);
    this.rot = new Float32Array(cap);
    this.spin = new Float32Array(cap);
    this.kind = new Uint8Array(cap);
    this.wasAlive = new Uint8Array(cap);

    const material = kit.track(new MeshBasicMaterial({ color: 0xffffff }));
    const spriteMaterial = kit.track(new MeshBasicMaterial({ color: 0xffffff, vertexColors: true }));
    const geometries: BufferGeometry[] = [geo.ico(1, 0), ...SPRITE_SHAPES.map((sh) => geo.get(`fx:sprite:${sh}`, () => buildSprite(sh)))];
    geometries.forEach((geometry, layer) => {
      const mesh = new InstancedMesh(geometry, layer === 0 ? material : spriteMaterial, cap);
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      for (let i = 0; i < cap; i++) mesh.setMatrixAt(i, this.zero);
      mesh.instanceColor = new InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
      mesh.instanceColor.setUsage(DynamicDrawUsage);
      mesh.frustumCulled = false;
      mesh.count = 0;
      mesh.visible = false;
      mesh.renderOrder = 2;
      scene.add(mesh);
      this.layers.push(mesh);
    });

    const ringGeo = geo.get('fx:ring', () => {
      const g = new TorusGeometry(1, 0.07, 6, 40);
      g.rotateX(Math.PI / 2);
      return g;
    });
    for (let i = 0; i < RING_POOL; i++) {
      const mat = kit.track(new MeshBasicMaterial({ color: EXPLOSION_COLORS[0] ?? '#ff9a3c', transparent: true, opacity: 0, depthWrite: false }));
      const mesh = new Mesh(ringGeo, mat);
      mesh.visible = false;
      mesh.frustumCulled = false;
      mesh.position.y = 0.1;
      mesh.renderOrder = 2;
      scene.add(mesh);
      this.rings.push({ mesh, material: mat, t: RING_SECONDS, radius: 1 });
    }
  }

  /** S'abonne aux evenements listes ci-dessus. `muzzleOf(catId)` fournit la position monde Three du muzzle. */
  subscribe(bus: EventBus<GameEvents>, muzzleOf: MuzzleLookup): void {
    this.muzzleOf = muzzleOf;
    const on = this.unsubs;
    on.push(
      bus.on('CatsRebuilt', (e) => {
        this.leaderId = e.catIds[0] ?? null;
      }),
      bus.on('ProjectileFired', (e) => this.muzzleFlash(e.catId, e.behavior, e.x, e.y, e.angle)),
      bus.on('BeamFired', (e) => {
        this.muzzleFlash(e.catId, e.behavior, e.x0, e.y0, Math.atan2(e.y1 - e.y0, e.x1 - e.x0));
        this.beamParticles(e.behavior, e.x0, e.y0, e.x1, e.y1);
      }),
      bus.on('EntityHit', (e) => this.hit(e.x, e.y, e.crit, e.behavior)),
      bus.on('Explosion', (e) => this.explosion(e.x, e.y, e.radius, e.behavior)),
      bus.on('TargetKilled', (e) => this.targetKilled(e.defId, e.family, e.x, e.y, e.isBoss, e.money.gt(0))),
      bus.on('RewardShown', (e) => {
        if (e.x !== undefined && e.y !== undefined) this.bills(e.x, e.y, 4);
      }),
      bus.on('LevelCleared', () => this.confetti()),
      bus.on('Firework', (e) => {
        if (this.ownFireworks) this.launchFirework(e.x, e.y, e.style, e.show);
      }),
      bus.on('CatnipCollected', (e) => this.catnipBurst(e.x, e.y)),
    );
  }

  /**
   * Particule de traine d'un projectile du skin `style` (coordonnees Three ; dirX/dirZ = direction de vol horizontale, 0 si inconnue).
   * Appelee de facon echantillonnee par ProjectileRenderer ; ignoree si le systeme est charge.
   */
  trail(style: AmmoStyle, x: number, y: number, z: number, dirX: number, dirZ: number): void {
    if (this.load() > 0.75) return;
    const bx = x - dirX * 0.15;
    const bz = z - dirZ * 0.15;
    const jx = rnd(-0.5, 0.5);
    const jz = rnd(-0.5, 0.5);
    const c = style.colors[Math.random() < 0.5 ? 0 : 1];
    switch (style.trail) {
      case 'none':
        break;
      case 'smoke':
        this.spawn(Kind.Smoke, x, y, z, jx * 0.4, 0.3, jz * 0.4, 0.5, 0.16, '#d9d4de', 0, 1.5, 0, 0);
        break;
      case 'sparkle':
        this.spawn(Kind.Sparkle, bx + jx * 0.25, y + rnd(-0.1, 0.15), bz + jz * 0.25, jx * 0.5, rnd(0, 0.5), jz * 0.5, rnd(0.35, 0.55), rnd(0.09, 0.14), c, 0, 0.6, Math.random() * 6, rnd(9, 15));
        break;
      case 'embers':
        this.spawn(Kind.Ember, bx + jx * 0.15, y, bz + jz * 0.15, jx * 0.5, rnd(0.8, 1.6), jz * 0.5, rnd(0.4, 0.65), rnd(0.05, 0.08), style.colors[Math.random() < 0.65 ? 1 : 0], -1.5, 0.8, 0, 0);
        break;
      case 'bubbles':
        this.spawn(Kind.Bubble, bx + jx * 0.2, y, bz + jz * 0.2, jx * 0.4, rnd(0.6, 1.2), jz * 0.4, rnd(0.6, 0.9), rnd(0.07, 0.12), c, -0.3, 0.8, 0, 0);
        break;
      case 'hearts':
        this.spawn(Kind.Heart, bx + jx * 0.2, y + 0.1, bz + jz * 0.2, jx * 0.4, rnd(0.7, 1.3), jz * 0.4, rnd(0.55, 0.85), rnd(0.09, 0.14), c, -0.4, 0.8, rnd(-0.3, 0.3), 0);
        break;
      case 'petals':
        this.spawn(Kind.Petal, bx + jx * 0.2, y, bz + jz * 0.2, jx * 0.6, rnd(0.2, 0.7), jz * 0.6, rnd(0.7, 1.0), rnd(0.08, 0.12), c, 1.2, 1.2, Math.random() * 6, rnd(-8, 8));
        break;
      case 'snow':
        this.spawn(Kind.Flake, bx + jx * 0.2, y, bz + jz * 0.2, jx * 0.5, rnd(0.2, 0.6), jz * 0.5, rnd(0.6, 0.9), rnd(0.07, 0.11), c, 0.9, 1.4, Math.random() * 6, rnd(-5, 5));
        break;
      case 'rainbow':
        this.spawn(Kind.Dot, bx, y, bz, 0, 0.05, 0, 0.5, 0.1, rainbowInto(this.color, this.clock * 1.4), 0, 0, 0, 0);
        break;
    }
  }

  /** Integre les particules (dt reel), ecrit les matrices d'instance. */
  update(dt: number): void {
    const step = Math.min(dt, 0.05);
    this.clock += step;
    // anneaux d'explosion
    for (const r of this.rings) {
      if (r.t >= RING_SECONDS) continue;
      r.t += step;
      const k = Math.min(1, r.t / RING_SECONDS);
      const ease = 1 - (1 - k) * (1 - k);
      const sc = 0.3 + (r.radius - 0.3) * ease;
      r.mesh.scale.set(sc, 1, sc);
      r.material.opacity = (1 - k) * 0.9;
      if (k >= 1) r.mesh.visible = false;
    }

    this.updateRockets(step);
    this.updateTrailers(step);

    // chef (cible des billets)
    let haveLeader = false;
    if (this.billsAlive > 0 && this.leaderId !== null) {
      const lp = this.muzzleOf(this.leaderId);
      if (lp) {
        this.leaderPos.set(lp.x, lp.y, lp.z);
        haveLeader = true;
      }
    }

    const camQ = this.camera.camera.quaternion;
    this.layerAlive.fill(0);
    let top = 0;
    let alive = 0;
    let bills = 0;
    for (let i = 0; i < this.highWater; i++) {
      const kind = this.kind[i] as Kind;
      const layer = this.layers[LAYER_OF[kind] ?? 0];
      if (!layer) continue;
      const l = this.life[i] ?? 0;
      if (l <= 0) {
        if (this.wasAlive[i]) {
          this.wasAlive[i] = 0;
          layer.setMatrixAt(i, this.zero);
        }
        continue;
      }
      const maxLife = this.maxLife[i] ?? 1;
      const nl = l - step;
      this.life[i] = nl;
      let x = this.px[i] ?? 0;
      let y = this.py[i] ?? 0;
      let z = this.pz[i] ?? 0;
      let vx = this.vx[i] ?? 0;
      let vy = this.vy[i] ?? 0;
      let vz = this.vz[i] ?? 0;
      const t = 1 - nl / maxLife;
      let dead = nl <= 0;

      if (kind === Kind.Bill) {
        if (t > BILL_HOMING_START && haveLeader) {
          const dx = this.leaderPos.x - x;
          const dy = this.leaderPos.y - y;
          const dz = this.leaderPos.z - z;
          const dist = Math.hypot(dx, dy, dz);
          if (dist < 0.5) dead = true;
          else {
            const speed = 7 + 16 * ((t - BILL_HOMING_START) / (1 - BILL_HOMING_START));
            const k = 1 - Math.exp(-9 * step);
            vx += ((dx / dist) * speed - vx) * k;
            vy += ((dy / dist) * speed - vy) * k;
            vz += ((dz / dist) * speed - vz) * k;
          }
        } else {
          vy -= (this.gravity[i] ?? 0) * step;
        }
        bills++;
      } else {
        vy -= (this.gravity[i] ?? 0) * step;
        // petales / flocons / bulles : flottement lateral
        if (kind === Kind.Petal || kind === Kind.Flake) {
          vx += Math.cos(t * 11 + i * 1.7) * 2.2 * step;
          vz += Math.sin(t * 9 + i) * 1.6 * step;
        } else if (kind === Kind.Bubble) {
          vx += Math.cos(t * 8 + i * 1.3) * 0.9 * step;
          vz += Math.sin(t * 7 + i) * 0.9 * step;
        }
      }
      const dr = this.drag[i] ?? 0;
      if (dr > 0) {
        const f = Math.exp(-dr * step);
        vx *= f;
        vy *= f;
        vz *= f;
      }
      x += vx * step;
      y += vy * step;
      z += vz * step;
      if (y < GROUND_Y && kind !== Kind.Bill && kind !== Kind.Smoke && kind !== Kind.Glitter && kind !== Kind.Ember && kind !== Kind.Flame && kind !== Kind.Dot && kind !== Kind.Heart && kind !== Kind.Sparkle && kind !== Kind.Bubble) {
        y = GROUND_Y;
        if (kind === Kind.Spark || kind === Kind.Chip) {
          vy = -vy * 0.35;
          vx *= 0.7;
          vz *= 0.7;
        } else {
          vx = 0;
          vy = 0;
          vz = 0;
        }
      }
      this.px[i] = x;
      this.py[i] = y;
      this.pz[i] = z;
      this.vx[i] = vx;
      this.vy[i] = vy;
      this.vz[i] = vz;
      const angle = (this.rot[i] ?? 0) + (this.spin[i] ?? 0) * step;
      this.rot[i] = angle;

      if (dead) {
        this.life[i] = 0;
        this.wasAlive[i] = 0;
        layer.setMatrixAt(i, this.zero);
        if (kind === Kind.Bill) bills--;
        continue;
      }

      // taille selon la famille
      const base = this.size[i] ?? 0.1;
      let sx = 1;
      let sy = 1;
      let sz = 1;
      let s = base;
      switch (kind) {
        case Kind.Flash:
          s = base * (1 - t * 0.7);
          sx = 0.8;
          sy = 0.8;
          sz = 1.9;
          break;
        case Kind.Smoke:
          s = base * (0.6 + 0.9 * t) * (1 - t * t);
          break;
        case Kind.Leaf:
          sx = 1.5;
          sy = 0.25;
          sz = 1;
          s = base * (t > 0.8 ? 1 - (t - 0.8) / 0.2 : 1);
          break;
        case Kind.Bill:
          sx = 1.7;
          sy = 0.12;
          sz = 1.1;
          s = base * (t > 0.85 ? 1 - (t - 0.85) / 0.15 : 1);
          break;
        case Kind.Confetti:
          sx = 1.3;
          sy = 0.1;
          sz = 0.8;
          s = base * (t > 0.85 ? 1 - (t - 0.85) / 0.15 : 1);
          break;
        case Kind.Glitter:
          s = base * (0.4 + 0.6 * Math.abs(Math.sin(angle))) * (1 - t * t);
          break;
        case Kind.Chip:
        case Kind.Spark:
          s = base * (1 - t * 0.6);
          break;
        case Kind.Ember:
        case Kind.Dot:
          s = base * (1 - t * 0.85);
          break;
        case Kind.Flame:
          s = base * (1 + 0.5 * t) * (1 - t * t);
          break;
        case Kind.Heart:
        case Kind.Star:
        case Kind.Petal:
          s = base * Math.min(1, t * 7) * (t > 0.7 ? (1 - t) / 0.3 : 1);
          break;
        case Kind.Sparkle:
          s = base * (0.3 + 0.7 * Math.abs(Math.sin(angle))) * (1 - t * t);
          break;
        case Kind.Bubble:
          s = base * (0.7 + 0.3 * Math.min(1, t * 5)) * (t > 0.86 ? 1 + ((t - 0.86) / 0.14) * 0.5 : 1);
          break;
        case Kind.Flake:
          s = base * (1 - t * t);
          break;
      }
      this.p.set(x, y, z);
      if (kind >= FIRST_SPRITE) {
        this.qRoll.setFromAxisAngle(AXIS_Z, angle);
        this.q.copy(camQ).multiply(this.qRoll);
      } else {
        if (kind === Kind.Flash) this.e.set(0, angle, 0);
        else this.e.set(angle, angle * 0.7, angle * 1.3);
        this.q.setFromEuler(this.e);
      }
      this.s.set(s * sx, s * sy, s * sz);
      this.m.compose(this.p, this.q, this.s);
      layer.setMatrixAt(i, this.m);
      this.wasAlive[i] = 1;
      this.layerAlive[LAYER_OF[kind] ?? 0] = (this.layerAlive[LAYER_OF[kind] ?? 0] ?? 0) + 1;
      alive++;
      top = i + 1;
    }
    this.billsAlive = bills;
    this.aliveCount = alive;
    this.highWater = Math.max(top, 0);
    for (let k = 0; k < LAYERS; k++) {
      const mesh = this.layers[k];
      if (!mesh) continue;
      const used = (this.layerAlive[k] ?? 0) > 0;
      mesh.count = this.highWater;
      mesh.visible = used;
      if (!used) continue;
      mesh.instanceMatrix.needsUpdate = true;
      if (this.layerDirty[k] && mesh.instanceColor) {
        mesh.instanceColor.needsUpdate = true;
        this.layerDirty[k] = 0;
      }
    }
  }

  setEco(eco: boolean): void {
    this.eco = eco;
    this.updateLimit();
  }

  /** Reglage Qualite : part des particules (0..1), cumulee avec le mode Eco. */
  setDetail(scale: number): void {
    this.detail = Math.min(1, Math.max(0.2, scale));
    this.updateLimit();
  }

  private updateLimit(): void {
    this.limit = Math.max(16, Math.floor(this.capacity * (this.eco ? 0.4 : 1) * this.detail));
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs = [];
    for (const mesh of this.layers) {
      this.scene.remove(mesh);
      mesh.dispose();
    }
    this.layers.length = 0;
    for (const r of this.rings) this.scene.remove(r.mesh);
    this.rings.length = 0;
    for (const r of this.rockets) {
      r.active = false;
      r.style = null;
      r.show = null;
    }
    for (const t of this.trailers) t.active = false;
    this.highWater = 0;
  }

  // ------------------------------------------------------------------------------------------------------------------

  /** Reserve une particule libre (anneau de recherche) ; -1 si le systeme est sature. */
  private alloc(): number {
    const n = this.limit;
    for (let k = 0; k < n; k++) {
      const i = (this.cursor + k) % n;
      if ((this.life[i] ?? 0) <= 0) {
        this.cursor = (i + 1) % n;
        return i;
      }
    }
    return -1;
  }

  /** Taux d'occupation (mesure a la derniere frame : pas de parcours). */
  private load(): number {
    return this.aliveCount / this.limit;
  }

  /** Nombre de particules d'une rafale : reduit en mode Eco. */
  private count(n: number): number {
    return Math.max(1, Math.round(n * (this.eco ? 0.6 : 1) * this.detail));
  }

  private spawn(kind: Kind, x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, size: number, color: string | Color, gravity: number, drag: number, rot: number, spin: number): void {
    const i = this.alloc();
    if (i < 0) return;
    this.px[i] = x;
    this.py[i] = y;
    this.pz[i] = z;
    this.vx[i] = vx;
    this.vy[i] = vy;
    this.vz[i] = vz;
    this.life[i] = life;
    this.maxLife[i] = life;
    this.size[i] = size;
    this.gravity[i] = gravity;
    this.drag[i] = drag;
    this.rot[i] = rot;
    this.spin[i] = spin;
    this.kind[i] = kind;
    this.wasAlive[i] = 1;
    if (i >= this.highWater) this.highWater = i + 1;
    const layer = LAYER_OF[kind] ?? 0;
    const attr = this.layers[layer]?.instanceColor;
    if (attr) {
      (typeof color === 'string' ? colorOf(color) : color).toArray(attr.array, i * 3);
      this.layerDirty[layer] = 1;
    }
  }

  private muzzleFlash(catId: number, behavior: keyof typeof CONFIG.behaviors, x: number, y: number, angle: number): void {
    const m = this.muzzleOf(catId);
    const fx = Math.cos(angle);
    const fz = -Math.sin(angle);
    const px = m ? m.x : x;
    const py = m ? m.y : 0.5;
    const pz = m ? m.z : -y;
    const seconds = CONFIG.behaviors[behavior].muzzleFlashSeconds;
    const style = this.ammoStyleOf(behavior);
    const c0 = style.rainbow ? rainbowInto(this.color, this.clock * 0.9) : style.colors[0];
    this.spawn(Kind.Flash, px + fx * 0.14, py, pz + fz * 0.14, 0, 0, 0, seconds, 0.42, c0, 0, 0, angle + Math.PI / 2, 0);
    this.spawn(Kind.Flash, px + fx * 0.18, py, pz + fz * 0.18, 0, 0, 0, seconds, 0.24, '#ffffff', 0, 0, angle + Math.PI / 2, 0);
    if (Math.random() < 0.5 && this.load() < 0.8) {
      // douille ejectee vers la droite du chat
      const rx = -fz;
      const rz = fx;
      this.spawn(Kind.Spark, px, py, pz, rx * 2.4 + (Math.random() - 0.5), 3 + Math.random() * 1.5, rz * 2.4 + (Math.random() - 0.5), 0.55, 0.05, CASING_COLOR, 14, 0, Math.random() * 6, 12);
    }
  }

  /** Particules de traine le long d'un faisceau (selon `trail` du skin ; rien pour none / smoke). */
  private beamParticles(behavior: WeaponBehavior, x0: number, y0: number, x1: number, y1: number): void {
    const style = this.ammoStyleOf(behavior);
    if (style.trail === 'none' || style.trail === 'smoke' || this.load() > 0.7) return;
    const len = Math.hypot(x1 - x0, y1 - y0);
    const n = this.count(Math.min(12, Math.floor(len / 1.2)));
    for (let k = 0; k < n; k++) {
      const t = (k + Math.random()) / n;
      this.trail(style, x0 + (x1 - x0) * t, 0.6, -(y0 + (y1 - y0) * t), 0, 0);
    }
  }

  /** Impact d'un projectile : jeu de particules du skin de la categorie `behavior`. */
  private hit(x: number, y: number, crit: boolean, behavior: WeaponBehavior): void {
    if (!crit && this.load() > 0.8) return;
    const style = this.ammoStyleOf(behavior);
    this.burst(style, x, 0.8, -y, this.count(this.baseCount(style.impact) * (crit ? 1.8 : 1)), crit ? 1.4 : 1, 0.1);
    if (crit) this.camera.shake(0.05);
  }

  private baseCount(impact: AmmoStyle['impact']): number {
    switch (impact) {
      case 'spark':
        return 5;
      case 'flame':
        return 5;
      case 'confetti':
        return 6;
      case 'hearts':
        return 3;
      case 'stars':
        return 4;
      case 'bubbles':
        return 4;
      case 'petals':
        return 4;
      case 'snow':
        return 5;
    }
  }

  /** Rafale de `n` particules du jeu d'impact du skin autour de (x, y, z) (position tiree dans un disque de rayon `spread`). */
  private burst(style: AmmoStyle, x: number, y: number, z: number, n: number, k: number, spread: number): void {
    const rainbow = style.rainbow === true;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      const d = Math.sqrt(Math.random()) * spread;
      const bx = x + ca * d;
      const bz = z + sa * d;
      // couleur alternee principale / secondaire (arc-en-ciel : teinte cyclique)
      const col: string | Color = rainbow ? rainbowInto(this.color, this.clock * 0.9 + i * 0.17) : style.colors[i % 2 === 0 ? 0 : 1];
      switch (style.impact) {
        case 'spark': {
          const sp = rnd(2, 6);
          const c = i % 2 === 0 ? '#ffffff' : col;
          this.spawn(Kind.Spark, bx, y, bz, ca * sp, rnd(1.5, 5), sa * sp, rnd(0.22, 0.4), rnd(0.09, 0.14) * k, c, 12, 0.5, 0, 0);
          break;
        }
        case 'flame':
          if (i % 5 < 3) this.spawn(Kind.Flame, bx, y, bz, ca * rnd(0.3, 1), rnd(1.2, 2.6), sa * rnd(0.3, 1), rnd(0.3, 0.48), rnd(0.15, 0.24) * k, col, -2, 1.5, 0, 0);
          else this.spawn(Kind.Ember, bx, y, bz, ca * rnd(1.5, 3), rnd(2, 4.5), sa * rnd(1.5, 3), rnd(0.45, 0.7), rnd(0.05, 0.08) * k, style.colors[1], 6, 0.6, 0, 0);
          break;
        case 'confetti': {
          const c = i % 3 === 2 && !rainbow ? (CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)] ?? '#ffffff') : col;
          this.spawn(Kind.Confetti, bx, y, bz, ca * rnd(1.5, 3.5), rnd(3, 6), sa * rnd(1.5, 3.5), rnd(0.6, 0.9), rnd(0.09, 0.13) * k, c, 10, 0.6, Math.random() * 6, rnd(-14, 14));
          break;
        }
        case 'hearts':
          this.spawn(Kind.Heart, bx, y, bz, ca * rnd(0.3, 1), rnd(1.6, 2.6), sa * rnd(0.3, 1), rnd(0.7, 1.0), rnd(0.17, 0.25) * k, col, -0.8, 0.8, rnd(-0.3, 0.3), 0);
          break;
        case 'stars':
          if (i % 3 === 2) this.spawn(Kind.Sparkle, bx, y, bz, ca * rnd(0.5, 1.5), rnd(0.5, 2), sa * rnd(0.5, 1.5), rnd(0.3, 0.5), rnd(0.12, 0.18) * k, '#ffffff', 0, 1, Math.random() * 6, rnd(10, 16));
          else this.spawn(Kind.Star, bx, y, bz, ca * rnd(2, 4), rnd(2.5, 5), sa * rnd(2, 4), rnd(0.5, 0.7), rnd(0.13, 0.19) * k, col, 7, 0.6, Math.random() * 6, rnd(-10, 10));
          break;
        case 'bubbles':
          this.spawn(Kind.Bubble, bx, y, bz, ca * rnd(0.5, 1.5), rnd(1, 2), sa * rnd(0.5, 1.5), rnd(0.8, 1.1), rnd(0.12, 0.2) * k, col, -1.2, 1.2, 0, 0);
          break;
        case 'petals':
          this.spawn(Kind.Petal, bx, y, bz, ca * rnd(1, 2.5), rnd(2, 4), sa * rnd(1, 2.5), rnd(0.8, 1.1), rnd(0.13, 0.19) * k, col, 4, 1.4, Math.random() * 6, rnd(-8, 8));
          break;
        case 'snow':
          this.spawn(Kind.Flake, bx, y, bz, ca * rnd(1, 2.5), rnd(1.5, 3), sa * rnd(1, 2.5), rnd(0.7, 1.0), rnd(0.1, 0.16) * k, col, 2, 1.6, Math.random() * 6, rnd(-6, 6));
          break;
      }
    }
  }

  private explosion(x: number, y: number, radius: number, behavior: WeaponBehavior): void {
    const style = this.ammoStyleOf(behavior);
    const ring = this.rings.find((r) => r.t >= RING_SECONDS);
    if (ring) {
      ring.t = 0;
      ring.radius = Math.max(0.8, radius);
      ring.mesh.position.set(x, 0.1, -y);
      ring.mesh.scale.set(0.3, 1, 0.3);
      ring.material.color.copy(style.rainbow ? rainbowInto(this.color, this.clock * 0.9) : colorOf(style.colors[0]));
      ring.material.opacity = 0.9;
      ring.mesh.visible = true;
    }
    // gerbe d'etincelles : palette de flammes (impact flamme) ou couleurs du skin
    const fire = style.impact === 'flame' || style.impact === 'spark';
    const sparks = this.count(16);
    for (let i = 0; i < sparks; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 3 + Math.random() * 5;
      const col = style.impact === 'flame' ? (EXPLOSION_COLORS[i % EXPLOSION_COLORS.length] ?? '#ff9a3c') : i % 3 === 2 ? '#ffffff' : style.rainbow ? rainbowInto(this.color, this.clock * 0.9 + i * 0.11) : style.colors[i % 2 === 0 ? 0 : 1];
      this.spawn(Kind.Spark, x, 0.5, -y, Math.cos(a) * sp, 2 + Math.random() * 3, Math.sin(a) * sp, 0.35 + Math.random() * 0.25, 0.18 + Math.random() * 0.14, col, 8, 2, 0, 0);
    }
    // particules du jeu d'impact, repandues sur la zone
    if (style.impact !== 'spark') this.burst(style, x, 0.6, -y, this.count(style.impact === 'flame' ? 9 : 12), 1.4, Math.max(0.4, radius * 0.5));
    const smoke = this.count(fire ? 4 : 2);
    for (let i = 0; i < smoke; i++) {
      const a = Math.random() * Math.PI * 2;
      this.spawn(Kind.Smoke, x + Math.cos(a) * radius * 0.3, 0.6, -y + Math.sin(a) * radius * 0.3, Math.cos(a) * 0.6, 1.0 + Math.random() * 0.8, Math.sin(a) * 0.6, 0.7, 0.5 + Math.random() * 0.3, fire ? '#8a8392' : '#cfc9d6', 0, 1.2, 0, 0);
    }
    this.camera.shake(0.12);
  }

  private targetKilled(defId: string, family: string, x: number, y: number, isBoss: boolean, hasMoney: boolean): void {
    const def = TARGET_BY_ID.get(defId);
    const pal = def?.visual.palette;
    const main = pal?.main ?? '#86d57c';
    const second = pal?.secondary ?? main;
    const accent = pal?.accent ?? '#ffffff';
    if (family === 'tree') {
      for (let i = 0; i < 10; i++) {
        const a = Math.random() * Math.PI * 2;
        const r = Math.random() * 0.6;
        this.spawn(Kind.Leaf, x + Math.cos(a) * r, 1.0 + Math.random() * 0.9, -y + Math.sin(a) * r, Math.cos(a) * (0.5 + Math.random()), 0.5 + Math.random() * 1.5, Math.sin(a) * (0.5 + Math.random()), 1.0 + Math.random() * 0.6, 0.12, i % 2 === 0 ? main : second, 3, 0.8, Math.random() * 6, (Math.random() - 0.5) * 10);
      }
    } else if (family === 'monster' || family === 'boss') {
      const n = isBoss ? 20 : 8;
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        const sp = 0.8 + Math.random() * 2.2;
        this.spawn(Kind.Smoke, x, 0.6, -y, Math.cos(a) * sp, 0.6 + Math.random() * 1.4, Math.sin(a) * sp, 0.45 + Math.random() * 0.15, 0.3 + Math.random() * 0.2, i % 3 === 0 ? accent : '#ffffff', 0, 2, 0, 0);
      }
      if (isBoss) this.camera.shake(0.12);
    } else if (family === 'rock' || family === 'ore') {
      for (let i = 0; i < 9; i++) {
        const a = Math.random() * Math.PI * 2;
        const sp = 1.5 + Math.random() * 3;
        this.spawn(Kind.Chip, x, 0.5, -y, Math.cos(a) * sp, 2 + Math.random() * 3, Math.sin(a) * sp, 0.55, 0.1 + Math.random() * 0.06, i % 3 === 0 ? accent : i % 3 === 1 ? main : second, 14, 0.3, Math.random() * 6, (Math.random() - 0.5) * 14);
      }
    }
    if (def?.visual.sparkle) {
      for (let i = 0; i < 14; i++) {
        const a = Math.random() * Math.PI * 2;
        const r = Math.random() * 0.9;
        this.spawn(Kind.Glitter, x + Math.cos(a) * r, 0.6 + Math.random() * 1.2, -y + Math.sin(a) * r, 0, 0.6 + Math.random() * 0.8, 0, 1.0 + Math.random() * 0.5, 0.09 + Math.random() * 0.05, i % 2 === 0 ? '#fff3b0' : '#ffffff', -0.3, 0.8, Math.random() * 6, 8 + Math.random() * 6);
      }
    }
    if (hasMoney) this.bills(x, y, 3 + Math.floor(Math.random() * 4));
  }

  private bills(x: number, y: number, n: number): void {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 1.5 + Math.random() * 2.5;
      this.spawn(Kind.Bill, x, 0.9, -y, Math.cos(a) * sp, 3 + Math.random() * 2.5, Math.sin(a) * sp, 1.0 + Math.random() * 0.35, 0.13, BILL_COLOR, 9, 0.6, Math.random() * 6, (Math.random() - 0.5) * 12);
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // Feu d'artifice (hub)

  /**
   * Lance la salve (`show.salvo` fusees decalees de SALVO_DELAY, positions un peu differentes) depuis la position sim (x, y) du chef :
   * (x, 0, -y) en monde Three. `show.useAmmo` : eclat « classique » aux couleurs / particules du skin de munition `style`.
   */
  /** Feux d'artifice du joueur local affiches (reglage « Mes effets »). */
  ownFireworks = true;

  /** Feu d'artifice d'un autre joueur du hub (meme rendu que chez lui ; le filtrage « effets des autres » est fait par l'appelant). */
  remoteFirework(x: number, y: number, style: AmmoStyle, show: FireworkStyle): void {
    this.launchFirework(x, y, style, show);
  }

  private launchFirework(x: number, y: number, style: AmmoStyle, show: FireworkStyle): void {
    const k = SHOW_SIZE_K[show.size - 1] ?? 1;
    for (let i = 0; i < show.salvo; i++) {
      let slot = this.rockets.find((r) => !r.active);
      if (!slot) {
        // tous les emplacements sont pris : la fusee la plus avancee eclate tout de suite (ou est simplement retiree si elle attend encore)
        slot = this.rockets.reduce((a, b) => (b.t / b.dur > a.t / a.dur ? b : a));
        if (slot.t >= 0) this.fireworkBurst(slot);
        else slot.active = false;
      }
      const off = i === 0 ? 0 : 1.8;
      slot.active = true;
      slot.t = -i * SALVO_DELAY;
      slot.dur = ROCKET_SECONDS * rnd(0.92, 1.1);
      slot.x = x + (off > 0 ? rnd(-off, off) : 0);
      slot.z = -y + (off > 0 ? rnd(-off, off) * 0.6 : 0);
      slot.peak = ROCKET_PEAK_MIN + (k - 1) * 1.6 + Math.random() * ROCKET_PEAK_VAR;
      slot.trailAcc = 0;
      slot.style = style;
      slot.show = show;
    }
    // petit panache de depart au pied du chef
    const n = this.count(5);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      this.spawn(Kind.Smoke, x + Math.cos(a) * 0.2, 0.3, -y + Math.sin(a) * 0.2, Math.cos(a) * 0.8, rnd(0.4, 1.0), Math.sin(a) * 0.8, 0.4, rnd(0.2, 0.3), '#e6e1ec', 0, 1.8, 0, 0);
    }
  }

  /** Altitude de la fusee a la fraction k du vol (montee qui ralentit vers le sommet). */
  private rocketY(r: Rocket, k: number): number {
    const e = 1 - (1 - k) * (1 - k);
    return ROCKET_START_Y + (r.peak - ROCKET_START_Y) * e;
  }

  /** Fait monter les fusees (tete + traine si `show.trail`) et declenche l'eclatement a l'arrivee. */
  private updateRockets(step: number): void {
    for (const r of this.rockets) {
      if (!r.active || !r.style) continue;
      r.t += step;
      if (r.t < 0) continue;
      const k = Math.min(1, r.t / r.dur);
      const y = this.rocketY(r, k);
      const px = r.x + Math.sin(k * 11 + r.x) * 0.12 * k;
      const colors = r.show && !r.show.useAmmo ? r.show.colors : r.style.colors;
      const rainbow = r.show && !r.show.useAmmo ? r.show.rainbow === true : r.style.rainbow === true;
      const trail = r.show ? r.show.trail : true;
      const head = rainbow ? rainbowInto(this.color, this.clock * 1.3) : colors[0];
      this.spawn(Kind.Dot, px, y, r.z, 0, 0, 0, 0.09, 0.17, '#ffffff', 0, 0, 0, 0);
      this.spawn(Kind.Dot, px, y - 0.12, r.z, 0, 0, 0, 0.1, 0.24, head, 0, 0, 0, 0);
      r.trailAcc += step;
      while (r.trailAcc >= ROCKET_TRAIL_EVERY) {
        r.trailAcc -= ROCKET_TRAIL_EVERY;
        if (!trail || this.load() > 0.85) continue;
        const ty = y - rnd(0.1, 0.5);
        const col = rainbow ? rainbowInto(this.color, this.clock * 1.3 + Math.random()) : colors[Math.random() < 0.5 ? 0 : 1];
        this.spawn(Kind.Ember, px + rnd(-0.08, 0.08), ty, r.z + rnd(-0.08, 0.08), rnd(-0.3, 0.3), rnd(-1.0, -0.3), rnd(-0.3, 0.3), rnd(0.28, 0.45), rnd(0.06, 0.1), col, 2.5, 1.2, 0, 0);
        if (Math.random() < 0.3) this.spawn(Kind.Smoke, px, ty - 0.1, r.z, rnd(-0.2, 0.2), rnd(-0.2, 0.2), rnd(-0.2, 0.2), 0.45, rnd(0.14, 0.22), '#ebe7f0', 0, 1.8, 0, 0);
      }
      if (k >= 1) this.fireworkBurst(r);
    }
  }

  /** Eclatement d'une fusee : spectacle (`show.pattern`) ou eclat classique du skin de munition (`useAmmo` / pas de spectacle). */
  private fireworkBurst(r: Rocket): void {
    r.active = false;
    const style = r.style;
    const show = r.show;
    r.style = null;
    r.show = null;
    if (!style) return;
    const x = r.x + Math.sin(11 + r.x) * 0.12;
    const y = this.rocketY(r, 1);
    const z = r.z;
    const camQ = this.camera.camera.quaternion;
    this.camRight.set(1, 0, 0).applyQuaternion(camQ);
    this.camUp.set(0, 1, 0).applyQuaternion(camQ);
    this.camView.set(0, 0, -1).applyQuaternion(camQ);
    if (show && !show.useAmmo) {
      this.showBurst(show, x, y, z);
      return;
    }
    this.classicBurst(style, x, y, z);
  }

  /** Eclat classique : petite lueur, anneau (face camera) de particules du skin d'impact, etincelles retombantes et scintillements (~38). */
  private classicBurst(style: AmmoStyle, x: number, y: number, z: number): void {
    const rainbow = style.rainbow === true;
    const col = (i: number): string | Color => (rainbow ? rainbowInto(this.color, this.clock * 0.9 + i * 0.06) : style.colors[i % 2 === 0 ? 0 : 1]);

    // lueur : deux boules qui se contractent vite
    this.spawn(Kind.Dot, x, y, z, 0, 0, 0, 0.22, 0.9, rainbow ? rainbowInto(this.color, this.clock) : style.colors[0], 0, 0, 0, 0);
    this.spawn(Kind.Dot, x, y, z, 0, 0, 0, 0.14, 0.55, '#ffffff', 0, 0, 0, 0);

    // anneau dans le plan de l'ecran : particules du jeu d'impact du skin
    const ring = this.count(15);
    const phase = Math.random() * Math.PI * 2;
    for (let i = 0; i < ring; i++) {
      const a = phase + (i / ring) * Math.PI * 2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      const sp = rnd(3.8, 4.4);
      const dx = (this.camRight.x * ca + this.camUp.x * sa) * sp;
      const dy = (this.camRight.y * ca + this.camUp.y * sa) * sp;
      const dz = (this.camRight.z * ca + this.camUp.z * sa) * sp;
      this.fireworkParticle(style, col(i), x, y, z, dx, dy, dz);
    }
    // etincelles : gerbe 3D qui retombe
    const sparks = this.count(15);
    for (let i = 0; i < sparks; i++) {
      const u = Math.random() * 2 - 1;
      const a = Math.random() * Math.PI * 2;
      const rr = Math.sqrt(1 - u * u);
      const sp = rnd(1.4, 4.2);
      this.spawn(Kind.Spark, x, y, z, Math.cos(a) * rr * sp, u * sp, Math.sin(a) * rr * sp, rnd(0.55, 0.95), rnd(0.07, 0.12), i % 3 === 2 ? '#ffffff' : col(i + 1), 6, 1.6, 0, 0);
    }
    // scintillements blancs au coeur
    this.glints(x, y, z, this.count(8));
  }

  private glints(x: number, y: number, z: number, n: number): void {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const b = Math.random() * Math.PI - Math.PI / 2;
      const sp = rnd(0.4, 1.4);
      this.spawn(Kind.Sparkle, x, y, z, Math.cos(a) * Math.cos(b) * sp, Math.sin(b) * sp, Math.sin(a) * Math.cos(b) * sp, rnd(0.45, 0.8), rnd(0.13, 0.2), '#ffffff', 1.2, 1.2, Math.random() * 6, rnd(10, 16));
    }
  }

  /** Une particule du jeu d'impact du skin lancee depuis l'eclatement (vitesse (dx, dy, dz) : s'etale puis retombe doucement). */
  private fireworkParticle(style: AmmoStyle, c: string | Color, x: number, y: number, z: number, dx: number, dy: number, dz: number): void {
    switch (style.impact) {
      case 'spark':
        this.spawn(Kind.Spark, x, y, z, dx, dy, dz, rnd(0.9, 1.2), rnd(0.12, 0.17), c, 3, 2.2, 0, 0);
        break;
      case 'flame':
        this.spawn(Kind.Flame, x, y, z, dx, dy, dz, rnd(0.7, 0.95), rnd(0.2, 0.28), c, 1.2, 2.2, 0, 0);
        break;
      case 'confetti':
        this.spawn(Kind.Confetti, x, y, z, dx, dy, dz, rnd(1.1, 1.5), rnd(0.12, 0.17), c, 4, 2.2, Math.random() * 6, rnd(-14, 14));
        break;
      case 'hearts':
        this.spawn(Kind.Heart, x, y, z, dx, dy, dz, rnd(1.1, 1.4), rnd(0.22, 0.3), c, 1.2, 2.2, rnd(-0.3, 0.3), 0);
        break;
      case 'stars':
        this.spawn(Kind.Star, x, y, z, dx, dy, dz, rnd(1.0, 1.3), rnd(0.2, 0.28), c, 2, 2.2, Math.random() * 6, rnd(-8, 8));
        break;
      case 'bubbles':
        this.spawn(Kind.Bubble, x, y, z, dx, dy, dz, rnd(1.2, 1.6), rnd(0.18, 0.26), c, -0.3, 2.4, 0, 0);
        break;
      case 'petals':
        this.spawn(Kind.Petal, x, y, z, dx, dy, dz, rnd(1.2, 1.6), rnd(0.2, 0.27), c, 1.8, 2.4, Math.random() * 6, rnd(-7, 7));
        break;
      case 'snow':
        this.spawn(Kind.Flake, x, y, z, dx, dy, dz, rnd(1.2, 1.6), rnd(0.18, 0.25), c, 1.2, 2.4, Math.random() * 6, rnd(-5, 5));
        break;
    }
  }

  /** Vecteur = a * droite + b * haut + c * profondeur (base de la camera), mis a l'echelle `k`, dans `fwV`. */
  private basis(a: number, b: number, c: number, k: number): Vector3 {
    const r = this.camRight;
    const u = this.camUp;
    const v = this.camView;
    return this.fwV.set((r.x * a + u.x * b + v.x * c) * k, (r.y * a + u.y * b + v.y * c) * k, (r.z * a + u.z * b + v.z * c) * k);
  }

  /** Couleur de la i-eme particule d'un spectacle (arc-en-ciel : teinte qui balaie l'eclat). */
  private showColor(show: FireworkStyle, i: number): string | Color {
    return show.rainbow ? rainbowInto(this.color, this.clock * 0.7 + i * 0.045) : show.colors[i % 2 === 0 ? 0 : 1];
  }

  /** Particule de forme (vitesse `v`, retombe en s'etalant : les formes gardent leurs proportions grace a une trainee identique). */
  private shape(show: FireworkStyle, i: number, x: number, y: number, z: number, v: Vector3, kind: Kind, size: number, life: number, gravity: number): void {
    const spin = kind === Kind.Star ? rnd(-6, 6) : 0;
    this.spawn(kind, x, y, z, v.x, v.y, v.z, life * rnd(0.92, 1.08), size, this.showColor(show, i), gravity, 2.2, kind === Kind.Heart ? rnd(-0.2, 0.2) : Math.random() * 6, spin);
  }

  /** Point i / n d'une sphere de Fibonacci (direction unitaire dans la base de la camera) x vitesse `k`. */
  private sphereDir(i: number, n: number, k: number): Vector3 {
    const yy = 1 - (2 * (i + 0.5)) / n;
    const rr = Math.sqrt(Math.max(0, 1 - yy * yy));
    const th = i * 2.399963;
    return this.basis(rr * Math.cos(th), yy, rr * Math.sin(th), k);
  }

  /** Eclat d'un spectacle : lueur + figure selon `show.pattern` (+ crepitements), dans la base de la camera. */
  private showBurst(show: FireworkStyle, x: number, y: number, z: number): void {
    const k = SHOW_SIZE_K[show.size - 1] ?? 1;
    const S = 3.7 * k;
    const head = this.showColor(show, 0);
    this.spawn(Kind.Dot, x, y, z, 0, 0, 0, 0.22, 0.9 * k, head, 0, 0, 0, 0);
    this.spawn(Kind.Dot, x, y, z, 0, 0, 0, 0.14, 0.55 * k, '#ffffff', 0, 0, 0, 0);
    switch (show.pattern) {
      case 'peony': {
        const n = this.count(34);
        for (let i = 0; i < n; i++) this.shape(show, i, x, y, z, this.sphereDir(i, n, S * rnd(0.85, 1)), Kind.Spark, 0.13, 1.2, 2.5);
        this.glints(x, y, z, this.count(6));
        break;
      }
      case 'ring': {
        const n = this.count(26);
        const ph = Math.random() * TAU;
        for (let i = 0; i < n; i++) {
          const a = ph + (i / n) * TAU;
          this.shape(show, i, x, y, z, this.basis(Math.cos(a), Math.sin(a), 0, S * 1.1), Kind.Spark, 0.13, 1.2, 2.2);
        }
        this.glints(x, y, z, this.count(6));
        break;
      }
      case 'heart': {
        const n = this.count(30);
        for (let i = 0; i < n; i++) {
          const t = (i / n) * TAU;
          const hx = (16 * Math.sin(t) ** 3) / 17;
          const hy = (13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)) / 17 + 0.05;
          this.shape(show, i, x, y, z, this.basis(hx, hy, 0, S * 1.15), Kind.Heart, 0.22, 1.4, 1.2);
        }
        break;
      }
      case 'star': {
        const n = this.count(30);
        for (let i = 0; i < n; i++) {
          const u = (i / n) * 10;
          const e = Math.floor(u);
          const f = u - e;
          const a0 = Math.PI / 2 + (e * Math.PI) / 5;
          const a1 = Math.PI / 2 + ((e + 1) * Math.PI) / 5;
          const r0 = e % 2 === 0 ? 1 : 0.42;
          const r1 = e % 2 === 0 ? 0.42 : 1;
          const px = Math.cos(a0) * r0 * (1 - f) + Math.cos(a1) * r1 * f;
          const py = Math.sin(a0) * r0 * (1 - f) + Math.sin(a1) * r1 * f;
          this.shape(show, i, x, y, z, this.basis(px, py, 0, S * 1.05), Kind.Star, 0.22, 1.3, 1.5);
        }
        break;
      }
      case 'spiral': {
        const n = this.count(32);
        for (let i = 0; i < n; i++) {
          const th = (i / n) * TAU * 2.6;
          const r = 0.08 + (i / n) * 0.95;
          this.shape(show, i, x, y, z, this.basis(Math.cos(th) * r, Math.sin(th) * r, 0, S * 1.2), Kind.Spark, 0.13, 1.3, 2);
        }
        this.glints(x, y, z, this.count(4));
        break;
      }
      case 'double': {
        const n1 = this.count(14);
        const n2 = this.count(24);
        for (let i = 0; i < n1; i++) this.shape(show, i * 2, x, y, z, this.sphereDir(i, n1, S * 0.5), Kind.Spark, 0.12, 1.1, 2.2);
        for (let i = 0; i < n2; i++) this.shape(show, i * 2 + 1, x, y, z, this.sphereDir(i, n2, S * 1.05), Kind.Spark, 0.13, 1.3, 2.4);
        break;
      }
      case 'saturn': {
        const n1 = this.count(14);
        const n2 = this.count(26);
        for (let i = 0; i < n1; i++) this.shape(show, i * 2, x, y, z, this.sphereDir(i, n1, S * 0.55), Kind.Spark, 0.13, 1.2, 2.2);
        const roll = -0.35;
        const tilt = 1.15;
        const cr = Math.cos(roll);
        const sr = Math.sin(roll);
        for (let i = 0; i < n2; i++) {
          const a = (i / n2) * TAU;
          const ca = Math.cos(a);
          const sa = Math.sin(a) ;
          // anneau incline : plan engendre par (droite, haut) tournes de `roll` autour de l'axe de visee, puis bascule de `tilt`
          const bx = ca * cr - sa * Math.cos(tilt) * sr;
          const by = ca * sr + sa * Math.cos(tilt) * cr;
          const bz = sa * Math.sin(tilt);
          this.shape(show, i * 2 + 1, x, y, z, this.basis(bx, by, bz, S * 1.15), Kind.Spark, 0.13, 1.3, 1.8);
        }
        break;
      }
      case 'willow': {
        const n = this.count(12);
        for (let i = 0; i < n; i++) this.trailer(show, i, x, y, z, this.sphereDir(i, n, S * 0.6), { gravity: 2.4, drag: 1.0, dur: 2.0, every: 0.12, size: 0.12 });
        break;
      }
      case 'crossette': {
        const n = this.count(7);
        for (let i = 0; i < n; i++) this.trailer(show, i, x, y, z, this.sphereDir(i, n, S * 0.8), { gravity: 3, drag: 1.4, dur: 0.55, every: 0.05, size: 0.12, split: true });
        break;
      }
      case 'palm': {
        const n = this.count(8);
        for (let i = 0; i < n; i++) {
          const phi = (i / Math.max(1, n - 1) - 0.5) * 2.4;
          this.trailer(show, i, x, y, z, this.basis(Math.sin(phi), Math.cos(phi), rnd(-0.3, 0.3), S * 0.95), { gravity: 4.5, drag: 0.7, dur: 1.5, every: 0.05, size: 0.15 });
        }
        this.glints(x, y, z, this.count(4));
        break;
      }
      case 'cascade': {
        const n = this.count(9);
        for (let i = 0; i < n; i++) {
          const v = this.basis(rnd(-1, 1) * 1.4, rnd(2.2, 3.2), rnd(-0.5, 0.5) * 1.4, k);
          this.trailer(show, i, x, y, z, v, { gravity: 5, drag: 0.25, dur: 1.3, every: 0.035, size: 0.13, sparkle: true });
        }
        break;
      }
    }
    if (show.crackle) this.trailer(show, 0, x, y, z, this.fwV.set(0, 0, 0), { gravity: 0, drag: 0, dur: 0.9, every: 0.06, size: 0.12, crackle: true, radius: 1.6 * k, start: -0.45 });
  }

  /** Reserve un emetteur balistique (pool) : vitesse initiale `v`, parametres dans `o`. */
  private trailer(show: FireworkStyle, i: number, x: number, y: number, z: number, v: Vector3, o: { gravity: number; drag: number; dur: number; every: number; size: number; split?: boolean; crackle?: boolean; sparkle?: boolean; radius?: number; start?: number }): void {
    const t = this.trailers.find((e) => !e.active);
    if (!t) return;
    t.active = true;
    t.t = o.start ?? 0;
    t.dur = o.dur;
    t.x = x;
    t.y = y;
    t.z = z;
    t.vx = v.x;
    t.vy = v.y;
    t.vz = v.z;
    t.gravity = o.gravity;
    t.drag = o.drag;
    t.acc = 0;
    t.every = o.every;
    t.size = o.size;
    t.rainbow = show.rainbow === true;
    t.hue = i * 0.09;
    t.color = show.colors[i % 2 === 0 ? 0 : 1];
    t.split = o.split === true;
    t.crackle = o.crackle === true;
    t.sparkle = o.sparkle === true;
    t.radius = o.radius ?? 0;
  }

  /** Integre les emetteurs : traine d'etincelles derriere eux, division en croix, crepitements. */
  private updateTrailers(step: number): void {
    const calm = this.aliveCount > FIREWORK_PARTICLE_BUDGET;
    for (const t of this.trailers) {
      if (!t.active) continue;
      t.t += step;
      if (t.t < 0) continue;
      if (!t.crackle) {
        t.vy -= t.gravity * step;
        const f = Math.exp(-t.drag * step);
        t.vx *= f;
        t.vy *= f;
        t.vz *= f;
        t.x += t.vx * step;
        t.y += t.vy * step;
        t.z += t.vz * step;
      }
      const col: string | Color = t.rainbow ? rainbowInto(this.color, this.clock * 0.9 + t.hue) : t.color;
      t.acc += step;
      while (t.acc >= t.every) {
        t.acc -= t.every;
        if (calm) continue;
        if (t.crackle) {
          for (let j = 0; j < 2; j++) {
            const u = Math.random() * 2 - 1;
            const a = Math.random() * Math.PI * 2;
            const rr = Math.sqrt(1 - u * u) * t.radius * Math.random();
            this.spawn(Kind.Glitter, t.x + Math.cos(a) * rr, t.y + u * t.radius * 0.6, t.z + Math.sin(a) * rr, 0, -0.2, 0, rnd(0.4, 0.7), rnd(0.1, 0.15), Math.random() < 0.5 ? '#ffffff' : col, 0.3, 0.8, Math.random() * 6, rnd(8, 14));
          }
        } else if (t.sparkle) {
          this.spawn(Kind.Sparkle, t.x + rnd(-0.06, 0.06), t.y, t.z + rnd(-0.06, 0.06), rnd(-0.3, 0.3), rnd(-0.3, 0.3), rnd(-0.3, 0.3), rnd(0.35, 0.55), t.size, col, 1, 1, Math.random() * 6, rnd(10, 16));
        } else {
          this.spawn(Kind.Ember, t.x, t.y, t.z, rnd(-0.25, 0.25), rnd(-0.4, 0.1), rnd(-0.25, 0.25), rnd(0.3, 0.45), t.size, col, 2.5, 1, 0, 0);
        }
      }
      if (!t.crackle) this.spawn(Kind.Spark, t.x, t.y, t.z, 0, 0, 0, 0.07, t.size * 1.5, col, 0, 0, 0, 0);
      if (t.t >= t.dur) {
        t.active = false;
        if (t.split) this.splitTrailer(t, col);
      }
    }
  }

  /** Division en croix d'une crossette : 4 petites etincelles dans le plan de l'ecran. */
  private splitTrailer(t: Trailer, col: string | Color): void {
    const hue = t.hue;
    const color = t.color;
    const rainbow = t.rainbow;
    const x = t.x;
    const y = t.y;
    const z = t.z;
    for (let j = 0; j < 4; j++) {
      const c = this.trailers.find((e) => !e.active);
      if (!c) return;
      const a = (j / 4) * TAU + Math.PI / 4;
      const sp = 2.4;
      this.basis(Math.cos(a), Math.sin(a), 0, sp);
      c.active = true;
      c.t = 0;
      c.dur = 0.5;
      c.x = x;
      c.y = y;
      c.z = z;
      c.vx = this.fwV.x;
      c.vy = this.fwV.y;
      c.vz = this.fwV.z;
      c.gravity = 3;
      c.drag = 1.5;
      c.acc = 0;
      c.every = 0.05;
      c.size = 0.08;
      c.rainbow = rainbow;
      c.hue = hue + j * 0.05;
      c.color = color;
      c.split = false;
      c.crackle = false;
      c.sparkle = false;
      c.radius = 0;
    }
    this.spawn(Kind.Dot, x, y, z, 0, 0, 0, 0.1, 0.3, col, 0, 0, 0, 0);
  }

  /** Gerbe de particules vertes / violettes a la cueillette d'une brindille d'herbe a chat. */
  private catnipBurst(x: number, y: number): void {
    const n = this.count(16);
    const colors = ['#7fe08a', '#c9a0ff', '#e6d0ff', '#4fbf6a'];
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = rnd(0.8, 2.4);
      const c = colors[i % colors.length] ?? '#7fe08a';
      if (i % 3 === 0) this.spawn(Kind.Leaf, x, 0.5, -y, Math.cos(a) * sp, rnd(1.5, 3), Math.sin(a) * sp, rnd(0.7, 1.0), 0.1, c, 4, 0.8, Math.random() * 6, rnd(-8, 8));
      else if (i % 3 === 1) this.spawn(Kind.Sparkle, x, 0.5, -y, Math.cos(a) * sp, rnd(1.2, 2.8), Math.sin(a) * sp, rnd(0.5, 0.8), rnd(0.12, 0.18), c, 1.5, 1, Math.random() * 6, rnd(10, 16));
      else this.spawn(Kind.Star, x, 0.5, -y, Math.cos(a) * sp, rnd(1.5, 3), Math.sin(a) * sp, rnd(0.6, 0.9), rnd(0.12, 0.16), c, 4, 0.8, Math.random() * 6, rnd(-6, 6));
    }
  }

  private confetti(): void {
    const lp = this.leaderId !== null ? this.muzzleOf(this.leaderId) : null;
    const cx = lp ? lp.x : 0;
    const cz = lp ? lp.z : 0;
    for (let i = 0; i < RENDER.fx.confettiCount; i++) {
      this.spawn(
        Kind.Confetti,
        cx + (Math.random() - 0.5) * 16,
        4 + Math.random() * 4,
        cz + (Math.random() - 0.5) * 12,
        (Math.random() - 0.5) * 2,
        -1 - Math.random() * 2,
        (Math.random() - 0.5) * 2,
        2.2 + Math.random() * 1.2,
        0.14,
        CONFETTI_COLORS[i % CONFETTI_COLORS.length] ?? '#ffffff',
        0.6,
        0.3,
        Math.random() * 6,
        (Math.random() - 0.5) * 14,
      );
    }
  }
}
